import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';

import useAxios from '../utils/useAxios';
import PageShell from './layout/PageShell';
import { EmptyState, NumberField, SectionCard, currency } from './ui';
import { errorText } from './OrderLines';

/** A product of TRUE's base catalog (Receipt-Web-Server api/base_catalog.py). */
export type BaseProduct = {
  barcode: string; name: string; brand: string; size: string; category: string; category_label: string;
  /** The business's product with this barcode, when it already sells it, and its price. */
  in_catalog: number | null; price: number | null;
};
type Listing = { results: BaseProduct[]; categories: { key: string; label: string }[]; packs: { key: string; label: string; description: string }[]; attribution: string };
type Pick = { product: BaseProduct; name: string; price: number | null };

/**
 * /admin/common-products: "Empieza con productos comunes". Common products sold in Mexico to pick from, by starter pack
 * (tiendita, cafetería…), category or search; tick them, write each price and add them all at once. Nothing is added
 * by itself.
 */
const BaseCatalog = () => {
  const axios = useAxios();
  const navigate = useNavigate();
  const [listing, setListing] = React.useState<Listing | null>(null);
  const [filter, setFilter] = React.useState<{ pack?: string; category?: string; q?: string }>({ pack: 'tiendita' });
  const [search, setSearch] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [picks, setPicks] = React.useState<Record<string, Pick>>({});
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [added, setAdded] = React.useState<number | null>(null);

  React.useEffect(() => {
    const timer = window.setTimeout(() => setFilter(f => (search.trim() ? { q: search.trim(), category: f.category } : f.q ? { pack: 'tiendita' } : f)), 300);
    return () => window.clearTimeout(timer);
  }, [search]);
  React.useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams(Object.entries(filter).filter(([, v]) => v) as [string, string][]);
    axios.get(`api/base-catalog/?${params}`).then(r => setListing(r.data)).catch(() => setListing(l => l)).finally(() => setLoading(false));
  }, [axios, filter]);

  const toggle = (product: BaseProduct) => setPicks(current => {
    if (current[product.barcode]) {
      const { [product.barcode]: _removed, ...rest } = current;
      return rest;
    }
    return { ...current, [product.barcode]: { product, name: product.name, price: null } };
  });
  const pickAll = () => setPicks(current => {
    const next = { ...current };
    (listing?.results ?? []).filter(p => !p.in_catalog && !next[p.barcode]).forEach(p => { next[p.barcode] = { product: p, name: p.name, price: null }; });
    return next;
  });
  const setPrice = (barcode: string, price: number | null) => setPicks(current => ({ ...current, [barcode]: { ...current[barcode], price } }));
  const chosen = Object.values(picks);
  const unpriced = chosen.filter(p => p.price == null).length;

  const add = async () => {
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.post('api/base-catalog/add/', { items: chosen.map(p => ({ barcode: p.product.barcode, price: p.price, name: p.name })) });
      setAdded((data.created ?? []).length);
      setPicks({});
      setFilter(f => ({ ...f }));  // marks them as in the catalog
    } catch (err) {
      setError(errorText(err, 'No se pudieron agregar los productos.'));
    } finally {
      setBusy(false);
    }
  };

  const results = listing?.results ?? [];
  return (
    <PageShell title="Productos comunes" subtitle="Empieza con productos que se venden en todo México: elige los tuyos, ponles precio y agrégalos.">
      <Stack spacing={2.5}>
        {added !== null && (
          <Alert severity="success" onClose={() => setAdded(null)} action={<Button color="inherit" onClick={() => navigate('/admin/products')}>Ver productos</Button>}>
            {added === 1 ? 'Agregamos 1 producto' : `Agregamos ${added} productos`} a tu catálogo. Tus cajas los reciben en un momento.
          </Alert>
        )}
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

        <Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>Según tu negocio</Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            {(listing?.packs ?? []).map(pack => (
              <Chip key={pack.key} label={pack.label} title={pack.description} data-testid={`pack-${pack.key}`}
                color={filter.pack === pack.key ? 'primary' : 'default'} onClick={() => { setSearch(''); setFilter({ pack: pack.key }); }} />
            ))}
          </Stack>
        </Box>
        <Stack spacing={1.5}>
          <TextField size="small" placeholder="Busca por nombre, marca o código de barras" value={search} onChange={e => setSearch(e.target.value)}
            sx={{ width: '100%', maxWidth: { md: 480 } }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }, htmlInput: { 'data-testid': 'base-search' } }} />
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75, alignItems: 'center' }}>
            {(listing?.categories ?? []).filter(c => c.key !== 'otros').map(c => (
              <Chip key={c.key} size="small" label={c.label} variant={filter.category === c.key ? 'filled' : 'outlined'}
                color={filter.category === c.key ? 'primary' : 'default'}
                onClick={() => setFilter(f => (f.category === c.key ? { q: f.q, pack: f.q ? undefined : 'tiendita' } : { q: f.q, category: c.key }))} />
            ))}
          </Stack>
        </Stack>

        <SectionCard noPadding
          title={filter.pack ? listing?.packs.find(p => p.key === filter.pack)?.label : filter.q ? `«${filter.q}»` : listing?.categories.find(c => c.key === filter.category)?.label}
          subtitle={filter.pack ? listing?.packs.find(p => p.key === filter.pack)?.description : undefined}
          action={results.some(p => !p.in_catalog) ? <Button size="small" onClick={pickAll}>Elegir todos</Button> : undefined}>
          {loading && !listing ? (
            <Box sx={{ py: 6, display: 'grid', placeItems: 'center' }}><CircularProgress size={28} /></Box>
          ) : results.length === 0 ? (
            <EmptyState compact icon={<StorefrontOutlinedIcon />} title="No encontramos productos" description="Prueba con otra palabra o regístralo tú en Agregar producto." />
          ) : (
            <Stack divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />} sx={{ opacity: loading ? 0.6 : 1 }}>
              {results.map(product => {
                const pick = picks[product.barcode];
                return (
                  <Stack key={product.barcode} direction="row" spacing={1.5} data-testid="base-product"
                    sx={{ px: 2, py: 1, alignItems: 'center', bgcolor: pick ? 'action.selected' : undefined }}>
                    <Checkbox checked={!!pick || !!product.in_catalog} disabled={!!product.in_catalog} onChange={() => toggle(product)}
                      slotProps={{ input: { 'aria-label': product.name } }} />
                    <Box sx={{ flex: 1, minWidth: 0, cursor: product.in_catalog ? 'default' : 'pointer' }} onClick={() => !product.in_catalog && toggle(product)}>
                      <Typography sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{product.name}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {product.in_catalog ? `Ya está en tu catálogo · ${currency(product.price)}` : `${product.category_label} · ${product.barcode}`}
                      </Typography>
                    </Box>
                    {pick && (
                      <NumberField size="small" label="Precio" value={pick.price} onChange={v => setPrice(product.barcode, v)} sx={{ width: 110, flexShrink: 0 }}
                        error={pick.price == null} slotProps={{ htmlInput: { 'data-testid': 'base-price' } }} />
                    )}
                  </Stack>
                );
              })}
            </Stack>
          )}
        </SectionCard>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {listing?.attribution ?? 'Datos: Open Food Facts (ODbL)'}: nombres, marcas y presentaciones de openfoodfacts.org. Revisa que coincidan con lo que vendes.
        </Typography>
        {chosen.length > 0 && (
          <Paper elevation={8} sx={{ position: 'sticky', bottom: 16, zIndex: 10, p: 1.5, borderRadius: 3 }}>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
              <Typography sx={{ flex: 1, fontWeight: 600 }}>
                {chosen.length === 1 ? '1 producto elegido' : `${chosen.length} productos elegidos`}
                {unpriced > 0 && <Typography component="span" variant="body2" sx={{ color: 'warning.main', ml: 1 }}>· {unpriced} sin precio</Typography>}
              </Typography>
              <Button onClick={() => setPicks({})} disabled={busy}>Quitar</Button>
              <Button variant="contained" onClick={add} disabled={busy || unpriced > 0} data-testid="base-add">
                {busy ? 'Agregando…' : `Agregar ${chosen.length}`}
              </Button>
            </Stack>
          </Paper>
        )}
      </Stack>
    </PageShell>
  );
};

export default BaseCatalog;
